import { useState, useEffect } from "react";
import { fetchPastEvents } from "src/api/payload/pastEvents";
import { getAllPastEventsData } from "src/utils/tournamentUtils";
import {
  isPayloadPastEventsSource,
  normalizePastEventsLocale,
} from "src/utils/pastEventsSource";

/**
 * Flattens the static per-year JSON shape (`{ [year]: { [key]: event } }`)
 * into the same flat array shape `mapPastEventsResponse` returns, so pages
 * consume one shape regardless of source.
 */
function flattenStaticYearsData(allYearsData) {
  return Object.entries(allYearsData || {}).flatMap(([year, yearEvents]) =>
    Object.entries(yearEvents || {}).map(([key, event]) => ({
      ...event,
      slug: event.slug || key,
      year: Number(year),
    }))
  );
}

/**
 * Loads all past events, from Payload CMS when `past-events` is in
 * `REACT_APP_PAYLOAD_SOURCES`, falling back silently to the static per-year
 * locale JSON on flag-off, empty CMS response, or API error — the page must
 * never render empty because the CMS is down (mirrors `useNavbar`).
 *
 * @param {string} locale - i18n language (`en` | `fr`)
 * @returns {{
 *   events: import('src/utils/mapPastEvents').PastEvent[],
 *   loading: boolean,
 *   error: string | null,
 *   source: 'payload' | 'static',
 * }}
 */
export function usePastEvents(locale) {
  const isPayloadSource = isPayloadPastEventsSource();
  const normalizedLocale = normalizePastEventsLocale(locale);
  const [events, setEvents] = useState([]);
  const [source, setSource] = useState(isPayloadSource ? "payload" : "static");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadStatic = async () => {
      try {
        const allYearsData = await getAllPastEventsData({
          language: normalizedLocale,
        });
        if (cancelled) return;
        setEvents(flattenStaticYearsData(allYearsData));
        setSource("static");
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching static past events:", err);
        setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const load = async () => {
      setLoading(true);
      setError(null);

      if (isPayloadSource) {
        try {
          const cmsEvents = await fetchPastEvents(normalizedLocale);
          if (cancelled) return;
          if (cmsEvents.length > 0) {
            setEvents(cmsEvents);
            setSource("payload");
            setLoading(false);
            return;
          }
        } catch (err) {
          if (cancelled) return;
          console.error("Error fetching past events from Payload:", err);
          setError(err?.message || "Failed to load past events");
        }
      }

      await loadStatic();
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isPayloadSource, normalizedLocale]);

  return { events, loading, error, source };
}
